import { isArray } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import {
  useIssuerPortfoliosInfiniteListQuery,
  useIssuerPortfoliosListQuery,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import { SelectPaginate, SelectPaginateOption } from 'ui';
import { PortfolioModel } from 'api';

type baseProps = {
  clearable?: boolean;
  label?: string;
  formFieldTitle?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  withBorder?: boolean;
  registerId?: number;
  placeholder?: string;
};

type singleProps = {
  isMulti?: false;
  onChange: (options: SelectPaginateOption<PortfolioModel> | null, fieldName: string) => void;
  value?: number;
};

type multiProps = {
  isMulti?: true;
  onChange: (options: SelectPaginateOption<PortfolioModel>[] | null, fieldName: string) => void;
  value?: number[];
};

type SelectPortfolioTransactionsProps = baseProps & (singleProps | multiProps);

export const SelectPortfolio = ({
  error,
  label,
  isMulti = false,
  disabled,
  clearable,
  className,
  withBorder = false,
  value,
  formFieldTitle = '',
  onChange,
  registerId,
  placeholder,
}: SelectPortfolioTransactionsProps) => {
  const [params] = useSearchParams();
  const register_id = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || 0);
  const { fetchNextPage } = useIssuerPortfoliosInfiniteListQuery(
    {
      page: 1,
      page_size: 10,
      register_id: registerId || register_id,
    },
    true,
  );
  const { data: dataPortfolio } = useIssuerPortfoliosListQuery({
    page: 1,
    page_size: 50,
    register_id: registerId || register_id,
  });

  const allPortfolio = useMemo<SelectPaginateOption<PortfolioModel>[]>(() => {
    return (dataPortfolio?.data || [])?.map(item => {
      const { name } = item;

      return {
        label: (
          <span className="flex gap-4 items-center justify-between font-[Inter] text-sm pr-3">
            <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
              <span className="text-gray-900">{name}</span>
            </span>
            <span></span>
          </span>
        ),
        value: item,
        toString: () => name,
      };
    });
  }, [dataPortfolio?.data]);

  const valueWithLabel = useMemo<
    SelectPaginateOption<PortfolioModel> | SelectPaginateOption<PortfolioModel>[] | null | undefined
  >(() => {
    if (!value) return null;

    const options = isArray(value)
      ? allPortfolio.filter(option => value?.includes(option.value.id))
      : allPortfolio.find(option => option.value.id === value);

    return options || null;
  }, [allPortfolio, value]);

  const handleAsyncLoadFormOptions = useCallback(
    async (page: number) => {
      const dataPortfolio = await fetchNextPage({ pageParam: page });

      const portfolio = dataPortfolio?.data?.pages?.at(-1)?.data || [];
      return portfolio.map(item => {
        const { name } = item;

        return {
          label: (
            <span className="flex gap-4 items-center justify-between font-[Inter] text-sm pr-3">
              <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                <span className="text-gray-900">{name}</span>
              </span>
              <span></span>
            </span>
          ),
          value: item,
          toString: () => name,
        };
      });
    },
    [fetchNextPage],
  );

  return (
    <SelectPaginate
      isClearable={clearable}
      disabled={disabled}
      isMulti={isMulti}
      label={label}
      className={className}
      onAsync={handleAsyncLoadFormOptions}
      onChange={options => onChange(options as any, formFieldTitle)}
      value={valueWithLabel}
      error={error}
      withBorder={withBorder}
      placeholder={placeholder}
    />
  );
};
